import React, { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";

import { useIntl } from "react-intl";

import { knownColors } from "@mapmycustomers/shared/enum/Color";
import AnyColor from "@mapmycustomers/shared/types/AnyColor";
import { Territory } from "@mapmycustomers/shared/types/entity";
import User from "@mapmycustomers/shared/types/User";
import { stopEvents } from "@mapmycustomers/shared/util/browser";
import { EditableText } from "@mapmycustomers/ui";

import InterceptNavigationModal from "@app/component/InterceptNavigationModal";
import ColorPicker from "@app/scene/map/components/ColorPicker";
import SharePicker from "@app/scene/map/components/SharePicker";
import BaseSidebar from "@app/scene/map/components/sidebar/Sidebar";
import useBoundsContext from "@app/scene/map/components/TerritoriesMode/utils/useBoundsContext";
import { exitTerritoryMode } from "@app/scene/map/store/territoryMode/actions";
import DemographicData from "@app/scene/map/types/DemographicData";
import { getCurrentUserId } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import { getTerritories } from "@app/store/territories";
import useAnalytics from "@app/util/contexts/useAnalytics";

import Loader from "../../Loader";
import Footer from "../../Sidebar/Footer";
import Header from "../../Sidebar/Header";

import styles from "./BaseCreateTerritorySidebar.module.scss";

interface Props {
  children?: ReactNode;
  count?: number;
  creating?: boolean;
  currentUserId: User["id"];
  loading?: boolean;
  onExitTerritoryMode: (forceExit?: true) => void;
  onSave?: () => void;
  onTerritoryChanged?: (
    name: string,
    color: AnyColor,
    opacity: number,
    userIds: User["id"][]
  ) => void;
  preventClicks?: boolean;
  regionDemographyData?: Omit<DemographicData, "code" | "type">;
  regionsSelected?: number;
  saveDisabled?: boolean | ReactNode; // ReactNode means "disabled" + show a tooltip with the given message
  shouldShowPrompt?: boolean;
  territories: Territory[];
}

const BaseCreateTerritorySidebar: React.FC<Props> = ({
  children,
  count,
  creating,
  currentUserId,
  loading,
  onExitTerritoryMode,
  onSave,
  onTerritoryChanged,
  preventClicks,
  regionDemographyData,
  regionsSelected,
  saveDisabled,
  shouldShowPrompt,
  territories,
}) => {
  const analytics = useAnalytics();
  useEffect(() => {
    analytics.initiated();
  }, [analytics]);

  const intl = useIntl();

  const handleClose = useCallback(() => {
    onExitTerritoryMode();
    analytics.cancelled("");
  }, [analytics, onExitTerritoryMode]);

  const formatName = useCallback(
    (index: number) =>
      intl.formatMessage(
        {
          id: "map.territories.sidebar.createDrawer",
          defaultMessage: "New Territory{index, select, 0 {} other { {index}}}",
          description: "Title of the Create Territory drawer in Territory Mode",
        },
        { index }
      ),
    [intl]
  );

  const firstFreeIndex = useMemo(() => {
    let i = 0;
    const currentNames = new Set(territories.map(({ name }) => name));
    while (i < 1000) {
      const name = formatName(i);
      if (!currentNames.has(name)) {
        return i;
      }
      i++;
    }
    return i;
  }, [formatName, territories]);

  const [name, setName] = useState(formatName(firstFreeIndex));
  const [color, setColor] = useState<AnyColor>(
    knownColors[Math.floor(Math.random() * knownColors.length)] // pick random color for a new territory
  );
  const [opacity, setOpacity] = useState<number>(0.5);
  const [selectedUserIds, setSelectedUserIds] = useState<User["id"][]>([]);

  const { setColor: setBannerColor } = useBoundsContext();
  useEffect(() => {
    setBannerColor(color);
  }, [color, setBannerColor]);

  useEffect(() => {
    onTerritoryChanged?.(name, color, opacity, selectedUserIds);
  }, [name, color, opacity, selectedUserIds, onTerritoryChanged]);

  const handleLeave = useCallback(() => onExitTerritoryMode(true), [onExitTerritoryMode]);

  return (
    <>
      <Loader
        loading={loading}
        regionDemographyData={regionDemographyData}
        regionsSelected={regionsSelected}
      />
      <BaseSidebar
        className={styles.container}
        onClose={handleClose}
        titleRaw={
          <EditableText
            fieldClassName={styles.nameField}
            onChange={setName}
            required
            size="default"
            value={name}
          />
        }
        visible
      >
        <Header
          className={styles.recordListHeader}
          count={count}
          countClassName={styles.recordCountClassName}
          extra={
            <div className={styles.properties}>
              <SharePicker
                creatorUserId={currentUserId}
                onChange={setSelectedUserIds}
                userIds={selectedUserIds}
              />
              <ColorPicker
                color={color}
                onColorChange={setColor}
                onOpacityChange={setOpacity}
                opacity={opacity}
                showColorSpace
                showInput
              />
            </div>
          }
        />
        <div className={styles.form} onClick={preventClicks ? stopEvents : undefined}>
          {children}
        </div>
        <Footer
          creating
          loading={creating}
          onCancel={handleClose}
          onSave={onSave}
          saveDisabled={saveDisabled}
        />
        <InterceptNavigationModal
          onLeave={handleLeave}
          shouldCloseOnLeave
          shouldShowPrompt={shouldShowPrompt ?? false}
        />
      </BaseSidebar>
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  currentUserId: getCurrentUserId(state)!,
  territories: getTerritories(state),
});

const mapDispatchToProps = {
  onExitTerritoryMode: exitTerritoryMode.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(BaseCreateTerritorySidebar);
